"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  List,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Fade,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Close,
  Save,
  Cancel,
  AttachMoney,
  People,
  Settings,
  TrendingUp,
  AccountBalance,
} from "@mui/icons-material";
import {
  addPayrollItem,
  addSalaryScales,
  getPayrollItems,
  getSalaryScalesByBranch,
  handleDeletePayrollItem,
  updatePayrollItem,
} from "../../../api/payroll/payrolls";
import { IonContent, IonHeader, IonPage } from "@ionic/react";

type PayrollItem = {
  id: string;
  name: string;
  type: "deduction" | "allowance";
};

type SalaryScale = {
  id: string;
  staffType: string;
  staffCategory: string;
  level: string;
  step: string;
  basic_salary: number;
  rent: number;
  peculiar: number;
};

const PayrollSettings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState<"payrollItems" | "salaryScales">(
    "payrollItems"
  );
  const [payrollItems, setPayrollItems] = useState<PayrollItem[]>([]);
  const [salaryScales, setSalaryScales] = useState<SalaryScale[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Payroll Item Form
  const [newPayrollItem, setNewPayrollItem] = useState<Omit<PayrollItem, "id">>(
    {
      name: "",
      type: "deduction",
    }
  );

  // Salary Scale Modal
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [newSalaryScales, setNewSalaryScales] = useState<
    Omit<SalaryScale, "id">[]
  >([
    {
      staffType: "",
      staffCategory: "",
      level: "",
      step: "",
      basic_salary: 0,
      rent: 0,
      peculiar: 0,
    },
  ]);

  // Editing states
  const [editingId, setEditingId] = useState<string | null>(null);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Payroll Item Functions
  const handleAddPayrollItem = async () => {
    if (!newPayrollItem.name) return;

    setLoading(true);
    try {
      const data = {
        name: newPayrollItem.name,
        type: newPayrollItem.type,
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };

      const response = await addPayrollItem(data);
      console.log(response);

      setPayrollItems([
        ...payrollItems,
        {
          id: Date.now().toString(),
          ...newPayrollItem,
        },
      ]);

      setNewPayrollItem({
        name: "",
        type: "deduction",
      });

      showSnackbar(`${newPayrollItem.type} added successfully!`);
    } catch (error) {
      showSnackbar("Failed to add item", "error");
    } finally {
      setLoading(false);
    }
  };

  // Salary Scale Functions
  const addNewScaleRow = () => {
    setNewSalaryScales([
      ...newSalaryScales,
      {
        staffType: "",
        staffCategory: "",
        level: "",
        step: "",
        basic_salary: 0,
        rent: 0,
        peculiar: 0,
      },
    ]);
  };

  const removeScaleRow = (index: number) => {
    if (newSalaryScales.length <= 1) return;
    const updated = [...newSalaryScales];
    updated.splice(index, 1);
    setNewSalaryScales(updated);
  };

  const handleAddSalaryScales = async () => {
    setLoading(true);
    try {
      const scalesWithIds = newSalaryScales.map((scale) => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        ...scale,
      }));

      const data = {
        scale: newSalaryScales,
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };

      const response = await addSalaryScales(data);
      console.log(response);

      if (response.data.status == true) {
        setSalaryScales([...salaryScales, ...scalesWithIds]);
        setNewSalaryScales([
          {
            staffType: "",
            staffCategory: "",
            level: "",
            step: "",
            basic_salary: 0,
            rent: 0,
            peculiar: 0,
          },
        ]);
        setShowScaleModal(false);
        showSnackbar("Salary scales added successfully!");
      }
    } catch (error) {
      showSnackbar("Failed to add salary scales", "error");
    } finally {
      setLoading(false);
    }
  };

  // Edit functions
  const handleEdit = (id: string) => {
    setEditingId(id);
    if (activeTab === "payrollItems") {
      const item = payrollItems.find((p) => p.id === id);
      if (item) setNewPayrollItem(item);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    setLoading(true);
    try {
      if (activeTab === "payrollItems") {
        const data = {
          name: newPayrollItem.name,
          type: newPayrollItem.type,
          business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
          branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
          payrollItem_id: editingId,
        };

        const response = await updatePayrollItem(data);
        console.log(response);

        setPayrollItems(
          payrollItems.map((item) =>
            item.id === editingId ? { id: editingId, ...newPayrollItem } : item
          )
        );

        showSnackbar("Item updated successfully!");
      }

      setEditingId(null);
      setNewPayrollItem({
        name: "",
        type: "deduction",
      });
    } catch (error) {
      showSnackbar("Failed to update item", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewPayrollItem({
      name: "",
      type: "deduction",
    });
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      const data = {
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };

      if (activeTab === "payrollItems") {
        await handleDeletePayrollItem(data);
        setPayrollItems(payrollItems.filter((p) => p.id !== id));
      } else {
        await handleDeletePayrollItem(data);
        setSalaryScales(salaryScales.filter((s) => s.id !== id));
      }

      showSnackbar("Item deleted successfully!");
    } catch (error) {
      showSnackbar("Failed to delete item", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollItems = async () => {
    setLoading(true);
    try {
      const data = {
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };
      const response = await getPayrollItems(data);
      console.log(response);
      setPayrollItems(response.data.data);
    } catch (error) {
      showSnackbar("Failed to fetch payroll items", "error");
    } finally {
      setLoading(false);
    }
  };

  const getSalaryScales = async () => {
    setLoading(true);
    try {
      const data = {
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };
      const response = await getSalaryScalesByBranch(data);
      console.log({ salary: response.data });
      setSalaryScales(response.data.data);
    } catch (error) {
      showSnackbar("Failed to fetch salary scales", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollItems();
    getSalaryScales();
  }, []);

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: "primary.main" }}
        >
          <Toolbar>
            <Avatar sx={{ mr: 2, bgcolor: "primary.dark" }}>
              <Settings />
            </Avatar>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Payroll Settings
            </Typography>
          </Toolbar>
        </AppBar>
      </IonHeader>
      <IonContent>
        <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
          {/* Header */}

          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                        <TrendingUp />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "success.main" }}
                        >
                          {
                            payrollItems.filter(
                              (item) => item.type === "allowance"
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Allowances
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "error.main", mr: 2 }}>
                        <AccountBalance />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "error.main" }}
                        >
                          {
                            payrollItems.filter(
                              (item) => item.type === "deduction"
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Deductions
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                        <People />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "info.main" }}
                        >
                          {salaryScales.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Salary Scales
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
                        <AttachMoney />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "warning.main" }}
                        >
                          ₦
                          {salaryScales
                            .reduce((sum, scale) => sum + scale.basic_salary, 0)
                            .toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Basic Salary
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Main Content */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: "hidden" }}>
              {/* Tabs */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant={isMobile ? "fullWidth" : "standard"}
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                      py: 2,
                    },
                  }}
                >
                  <Tab
                    label="Deductions/Allowances"
                    value="payrollItems"
                    icon={<AccountBalance />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Salary Scales"
                    value="salaryScales"
                    icon={<People />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              <CardContent sx={{ p: 0 }}>
                {activeTab === "payrollItems" ? (
                  <Fade in timeout={300}>
                    <Box>
                      {/* Add Form */}
                      <Card
                        sx={{
                          m: 3,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <CardHeader
                          title={editingId ? "Edit Item" : "Add New Item"}
                          sx={{ bgcolor: "grey.50" }}
                        />
                        <CardContent>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                required
                                fullWidth
                                label="Name"
                                placeholder="Enter name"
                                value={newPayrollItem.name}
                                onChange={(e) =>
                                  setNewPayrollItem({
                                    ...newPayrollItem,
                                    name: e.target.value,
                                  })
                                }
                                variant="outlined"
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl required fullWidth>
                                <FormLabel sx={{ mb: 1 }}>Type</FormLabel>
                                <RadioGroup
                                  row
                                  value={newPayrollItem.type}
                                  onChange={(e) =>
                                    setNewPayrollItem({
                                      ...newPayrollItem,
                                      type: e.target.value as
                                        | "deduction"
                                        | "allowance",
                                    })
                                  }
                                >
                                  <FormControlLabel
                                    value="deduction"
                                    control={<Radio />}
                                    label="Deduction"
                                  />
                                  <FormControlLabel
                                    value="allowance"
                                    control={<Radio />}
                                    label="Allowance"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 2,
                              mt: 3,
                            }}
                          >
                            {editingId ? (
                              <>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleSaveEdit}
                                  startIcon={
                                    loading ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <Save />
                                    )
                                  }
                                  disabled={loading || !newPayrollItem.name}
                                >
                                  Save Changes
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  onClick={handleCancelEdit}
                                  startIcon={<Cancel />}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddPayrollItem}
                                disabled={loading || !newPayrollItem.name}
                                startIcon={
                                  loading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <Add />
                                  )
                                }
                              >
                                Add{" "}
                                {newPayrollItem.type === "deduction"
                                  ? "Deduction"
                                  : "Allowance"}
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>

                      {/* Items Table */}
                      <Box sx={{ mx: 3, mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Current Deductions/Allowances
                        </Typography>

                        <TableContainer
                          component={Paper}
                          sx={{ borderRadius: 2, boxShadow: 2 }}
                        >
                          <Table>
                            <TableHead sx={{ bgcolor: "grey.100" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Type
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payrollItems?.map((item) => (
                                <TableRow key={item.id} hover>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={item.type.toUpperCase()}
                                      color={
                                        item.type === "deduction"
                                          ? "error"
                                          : "success"
                                      }
                                      size="small"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        onClick={() => handleEdit(item.id)}
                                        color="primary"
                                      >
                                        <Edit />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        onClick={() => handleDelete(item.id)}
                                        color="error"
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </Fade>
                ) : (
                  <Fade in timeout={300}>
                    <Box>
                      {/* Add Button */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          p: 3,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setShowScaleModal(true)}
                          startIcon={<Add />}
                          size="large"
                        >
                          Add Salary Scales
                        </Button>
                      </Box>

                      {/* Salary Scales Table */}
                      <Box sx={{ mx: 3, mb: 3 }}>
                        <TableContainer
                          component={Paper}
                          sx={{ borderRadius: 2, boxShadow: 2 }}
                        >
                          <Table>
                            <TableHead sx={{ bgcolor: "grey.100" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Staff Type
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Category
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Level
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Step
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Basic Salary
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Rent
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Peculiar
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {salaryScales.map((scale) => (
                                <TableRow key={scale.id} hover>
                                  <TableCell>{scale.staff_type}</TableCell>
                                  <TableCell>{scale.staff_category}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={scale.level}
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={scale.step}
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      color: "success.main",
                                    }}
                                  >
                                    ₦{scale.basic_salary.toLocaleString()}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontWeight: 600, color: "info.main" }}
                                  >
                                    ₦{scale.rent.toLocaleString()}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      color: "warning.main",
                                    }}
                                  >
                                    ₦{scale.peculiar.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        onClick={() => handleDelete(scale.id)}
                                        color="error"
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </Fade>
                )}
              </CardContent>
            </Card>
          </Container>

          {/* Salary Scale Modal */}
          <Dialog
            open={showScaleModal}
            onClose={() => setShowScaleModal(false)}
            fullWidth
            maxWidth="lg"
            PaperProps={{
              sx: { borderRadius: 3 },
            }}
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Add Multiple Salary Scales
                </Typography>
                <IconButton onClick={() => setShowScaleModal(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <List>
                {newSalaryScales.map((scale, index) => (
                  <React.Fragment key={index}>
                    <Card
                      sx={{
                        mb: 3,
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        Salary Scale {index + 1}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Staff Type*"
                            fullWidth
                            variant="outlined"
                            value={scale.staffType}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].staffType = e.target.value;
                              setNewSalaryScales(updated);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Staff Category*"
                            fullWidth
                            variant="outlined"
                            value={scale.staffCategory}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].staffCategory = e.target.value;
                              setNewSalaryScales(updated);
                            }}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="Level*"
                            fullWidth
                            variant="outlined"
                            value={scale.level}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].level = e.target.value;
                              setNewSalaryScales(updated);
                            }}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="Step*"
                            fullWidth
                            variant="outlined"
                            value={scale.step}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].step = e.target.value;
                              setNewSalaryScales(updated);
                            }}
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <TextField
                            label="Basic Salary*"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={scale.basic_salary}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].basic_salary =
                                Number.parseFloat(e.target.value) || 0;
                              setNewSalaryScales(updated);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₦
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <TextField
                            label="Rent*"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={scale.rent}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].rent =
                                Number.parseFloat(e.target.value) || 0;
                              setNewSalaryScales(updated);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₦
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={4}>
                          <TextField
                            label="Peculiar*"
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={scale.peculiar}
                            onChange={(e) => {
                              const updated = [...newSalaryScales];
                              updated[index].peculiar =
                                Number.parseFloat(e.target.value) || 0;
                              setNewSalaryScales(updated);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₦
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>

                      {index > 0 && (
                        <Box mt={2} display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => removeScaleRow(index)}
                          >
                            Remove This Scale
                          </Button>
                        </Box>
                      )}
                    </Card>

                    {index < newSalaryScales.length - 1 && (
                      <Divider sx={{ my: 2 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Add />}
                onClick={addNewScaleRow}
              >
                Add Another Scale
              </Button>

              <Box flexGrow={1} />

              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSalaryScales}
                disabled={
                  loading ||
                  newSalaryScales.some(
                    (scale) =>
                      !scale.staffType ||
                      !scale.staffCategory ||
                      !scale.level ||
                      !scale.step
                  )
                }
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              >
                Save All Scales
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default PayrollSettings;
